"use client";

import { useEffect, useState, useRef } from "react";
import { Link, usePathname } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ModeToggle } from "@/components/mode-toggle";
import LanguageSwitcher from "@/components/language-switcher";
import { useTranslations, useLocale } from "next-intl";
import { Settings } from "@/lib/api";
import { SafeImage } from "@/components/ui/safe-image";


export function Navbar({ initialSettings }: { initialSettings?: Settings | null }) {
    const t = useTranslations("Navbar");
    const locale = useLocale();
    const pathname = usePathname();
    const isHomePage = pathname === "/";
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [settings] = useState<Settings | null>(initialSettings ?? null);
    const [scrollProgress, setScrollProgress] = useState(0);
    
    useEffect(() => {
        const handleScroll = () => {
             setIsScrolled(window.scrollY > 50);
             const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
             setScrollProgress(maxScroll > 0 ? window.scrollY / maxScroll : 0);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const showScrolledStyle = isScrolled || !isHomePage;

    const controlBtnClass = showScrolledStyle 
        ? "text-foreground hover:bg-accent hover:text-accent-foreground" 
        : "text-white/90 hover:text-white hover:bg-white/10";

    const mobileControlBtnClass = (showScrolledStyle || isMobileMenuOpen) 
        ? "text-foreground hover:bg-accent hover:text-accent-foreground" 
        : "text-white/90 hover:text-white hover:bg-white/10";

    const navLinks = [
        { name: t("home"), href: "/" },
        { name: t("services"), href: "/#services" },
        { name: t("about"), href: "/p/about" },
        { name: t("process"), href: "/#process" }, 
        { name: t("blog"), href: "/blog" },
    ];

    return (
        <header className={cn(
            "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
            showScrolledStyle ? "py-2" : "py-6"
        )}>
            {/* Glossy Container */}
            <div className={cn(
                "container px-4 md:px-6 transition-all duration-500",
                showScrolledStyle ? "max-w-7xl" : "max-w-full"
            )}>
                <nav className={cn(
                    "flex items-center justify-between px-6 py-3 rounded-2xl transition-all duration-500 relative overflow-hidden",
                    showScrolledStyle 
                        ? "bg-background/80 backdrop-blur-xl border border-border/50 shadow-[0_8px_32px_0_rgba(0,0,0,0.1)]" 
                        : "bg-transparent border-transparent"
                )}>
                    {/* Inner Scroll Progress (Subtle) */}
                    <div
                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-secondary origin-left z-10 rtl:origin-right"
                        style={{ transform: `scaleX(${scrollProgress})` }}
                    />

                    <Link href="/" className="flex items-center gap-2 group transition-transform active:scale-95 z-20">
                        <SafeImage
                            src={(settings?.logo && settings.logo !== "") ? settings.logo : "/logo.webp"}
                            fallbackSrc="/logo.png"
                            alt={t("logo_alt")}
                            width={180}
                            height={112}
                            sizes="(max-width: 768px) 64px, 80px"
                            className={cn(
                                "h-10 md:h-12 w-auto object-contain transition-all duration-500",
                                (!showScrolledStyle && !settings?.logo) && "brightness-0 invert active:brightness-100 active:invert-0"
                            )}
                            priority
                            fetchPriority="high"
                        />
                    </Link>

                    <div className="hidden lg:flex items-center gap-10 z-20">
                         {navLinks.map((link) => (
                             <Link 
                                key={link.name} 
                                href={link.href}
                                className={cn(
                                    "text-sm font-bold transition-all hover:text-secondary relative group px-2 py-1",
                                    showScrolledStyle ? "text-foreground" : "text-white/90 hover:text-white"
                                )}
                             >
                                 {link.name}
                                 <span 
                                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-secondary origin-left rtl:origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-300" 
                                 />
                             </Link>
                         ))}
                         
                         <div className={cn(
                            "flex items-center gap-3 ms-4 ps-4 border-s transition-colors",
                            showScrolledStyle ? "border-border/50" : "border-white/20"
                         )}>
                            <div className="flex items-center gap-1">
                                <LanguageSwitcher className={controlBtnClass} />
                                <ModeToggle className={controlBtnClass} />
                            </div>
                            <Button 
                                variant={showScrolledStyle ? "default" : "secondary"} 
                                className={cn(
                                    "font-bold shadow-lg transition-all hover:scale-105 active:scale-95 px-8 rounded-xl h-11",
                                    !showScrolledStyle && "bg-white text-primary hover:bg-secondary hover:text-white"
                                )}
                                asChild
                            >
                                <Link href="#request-service">
                                    {t("contact")}
                                    <ArrowRight className="ms-2 w-4 h-4 rtl:rotate-180" />
                                </Link>
                            </Button>
                         </div>
                    </div>

                    {/* Mobile Controls */}
                    <div className="lg:hidden flex items-center gap-2 z-20">
                        <LanguageSwitcher className={mobileControlBtnClass} />
                        <ModeToggle className={mobileControlBtnClass} />
                        <button 

                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                            className={cn(
                                "lg:hidden relative z-50 h-12 w-12 flex items-center justify-center rounded-xl transition-all",
                                isMobileMenuOpen || showScrolledStyle 
                                    ? "text-foreground bg-accent/50 border-border hover:bg-accent" 
                                    : "text-white bg-white/10 border-white/20 hover:bg-white/20"
                            )}
                            aria-label={isMobileMenuOpen ? t("close_menu") : t("open_menu")}
                        >
                            <div className={cn(
                                "transition-all duration-200",
                                isMobileMenuOpen ? "rotate-90 scale-110" : "rotate-0 scale-100"
                            )}>
                                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                            </div>
                        </button>
                    </div>
                </nav>
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-slate-950/40 backdrop-blur-md lg:hidden z-40 animate-in fade-in duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <div
                        className={cn(
                            "absolute right-0 top-0 bottom-0 w-[80%] max-w-sm bg-background border-s shadow-2xl p-8 flex flex-col rtl:left-0 rtl:right-auto rtl:border-e rtl:border-s-0",
                            "transition-transform duration-300 ease-out",
                            isMobileMenuOpen ? "translate-x-0" : (locale === 'ar' ? "-translate-x-full" : "translate-x-full")
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-12">
                            <SafeImage src={settings?.logo || "/logo.webp"} fallbackSrc="/logo.png" alt={t("mobile_logo_alt")} width={140} height={88} sizes="(max-width: 768px) 64px, 80px" className="h-10 w-auto object-contain" />
                            <button onClick={() => setIsMobileMenuOpen(false)} className="h-12 w-12 flex items-center justify-center bg-accent rounded-full transition-transform active:scale-95">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="flex flex-col space-y-6">
                            {navLinks.map((link, index) => (
                                <div
                                    key={link.name}
                                    className="transition-all duration-300 ease-out"
                                    style={{
                                        transitionDelay: isMobileMenuOpen ? `${index * 100}ms` : '0ms',
                                        opacity: isMobileMenuOpen ? 1 : 0,
                                        transform: isMobileMenuOpen ? 'translateX(0)' : (locale === 'ar' ? 'translateX(20px)' : 'translateX(-20px)')
                                    }}
                                >
                                    <Link 
                                        href={link.href}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="text-2xl font-bold text-foreground hover:text-secondary transition-colors block border-b border-border/50 pb-4"
                                    >
                                        {link.name}
                                    </Link>
                                </div>
                            ))}
                            
                            <div
                                className="pt-8 transition-all duration-300 ease-out"
                                style={{
                                    transitionDelay: isMobileMenuOpen ? '500ms' : '0ms',
                                    opacity: isMobileMenuOpen ? 1 : 0,
                                    transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(20px)'
                                }}
                            >
                                <Button className="w-full h-16 text-xl font-bold shadow-2xl rounded-2xl group" asChild>
                                    <Link href="#request-service" onClick={() => setIsMobileMenuOpen(false)}>
                                        {t("contact")}
                                        <ArrowRight className="ms-2 group-hover:translate-x-2 transition-transform rtl:group-hover:-translate-x-2 rtl:rotate-180" />
                                    </Link>
                                </Button>
                            </div>
                        </div>

                        <div className="mt-auto pt-10 border-t border-border/50 text-center text-slate-600 dark:text-slate-400">
                            {t("portal")}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
